import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { MAHAVIDYAS, mvImageFor } from "@/lib/mahavidyas";
import { useSettings } from "@/context/SettingsContext";
import { MandalaMotif, Divider } from "@/components/MotifBackground";
import Seo from "@/components/Seo";
import { ArrowRight, Sparkles } from "lucide-react";

export default function MahavidyaHub() {
  const { pick, isDeva } = useLang();
  const settings = useSettings();
  const ugra = MAHAVIDYAS.filter((m) => m.group === "ugra");
  const saumya = MAHAVIDYAS.filter((m) => m.group === "saumya");

  const Card = ({ m }) => (
    <Link to={`/mahavidya/${m.slug}`} data-testid={`mahavidya-card-${m.slug}`} className="group flex flex-col overflow-hidden border border-line bg-surface transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-[0_12px_36px_rgba(44,38,33,0.12)]">
      <div className="relative aspect-[3/4] overflow-hidden bg-[var(--parchment)]">
        <img src={mvImageFor(m, settings)} alt={`${m.en} · ${m.deva}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-0 top-0 bg-[var(--ink)]/80 px-2.5 py-1 font-serif-latin text-xs text-white">{String(m.n).padStart(2, "0")}</span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="font-deva text-2xl text-ink deva-lh">{m.deva}</p>
        <p className="font-serif-latin text-ink-2">{m.en}</p>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-3">{pick(m.sig_hi, m.sig_en)}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5">{pick("विस्तार से", "Learn more")} <ArrowRight className="h-4 w-4" /></span>
      </div>
    </Link>
  );

  return (
    <div data-testid="mahavidya-hub" className="pb-24">
      <Seo path="/mahavidya" type="article"
        title="Dasha Mahavidya — The Ten Great Wisdom Goddesses · Mantras & Significance"
        description="The Dasha Mahavidya — Kali, Tara, Tripura Sundari, Bhuvaneshwari, Bhairavi, Chinnamasta, Dhumavati, Bagalamukhi, Matangi and Kamala. Iconography, significance, story and bija mantra of each of the ten wisdom goddesses of the Shakta-Tantric tradition."
        keywords="Dasha Mahavidya, ten Mahavidyas, Kali Tara Tripura Sundari Bhuvaneshwari Bhairavi Chinnamasta Dhumavati Bagalamukhi Matangi Kamala, Mahavidya mantra, wisdom goddesses, Shakta tantra" />

      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute -right-24 -top-24 hidden h-[520px] w-[520px] text-[var(--gold)] opacity-[0.05] md:block" />
        <div className="relative z-[2] mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-24">
          <p className="overline-label text-saffron">{pick("दस महाविद्या", "Dasha Mahavidya")}</p>
          <h1 className={`mt-4 text-4xl leading-[1.05] text-ink deva-lh md:text-6xl ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("दस महाविद्याएँ", "The Ten Wisdom Goddesses")}</h1>
          <div className="mt-6 h-px w-20 bg-[var(--gold)]/60" />
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-2">
            {pick(
              "शक्ति की दस महाविद्याएँ — काली से कमला तक — चेतना का सम्पूर्ण वर्णक्रम हैं: उग्र संहार एवं अतिक्रमण से लेकर सौम्य ऐश्वर्य तक। प्रत्येक स्वरूप एक विशिष्ट सत्य एवं मार्ग को प्रकट करता है।",
              "The ten Mahavidyas of Shakti — from Kali to Kamala — form a complete spectrum of consciousness: from fierce dissolution and transcendence to gentle, luminous abundance. Each reveals a distinct truth and a distinct path."
            )}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
        <div className="mb-8 flex items-baseline gap-3">
          <span className="h-px w-10 bg-[var(--gold)]" />
          <h2 className={`text-2xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("उग्र स्वरूप", "The Fierce (Ugra) forms")}</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{ugra.map((m) => <Card key={m.slug} m={m} />)}</div>

        <div className="mb-8 mt-16 flex items-baseline gap-3">
          <span className="h-px w-10 bg-[var(--gold)]" />
          <h2 className={`text-2xl text-saffron deva-lh ${isDeva ? "font-deva" : "font-serif-latin"}`}>{pick("सौम्य स्वरूप", "The Gentle (Saumya) forms")}</h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{saumya.map((m) => <Card key={m.slug} m={m} />)}</div>

        <Divider className="mx-auto my-16 max-w-xs" />
        <p className="mx-auto max-w-2xl text-center text-sm italic text-ink-3">
          {pick(
            "टिप्पणी: महाविद्या-साधना के पूर्ण मंत्र सम्प्रदाय-भेद से भिन्न होते हैं एवं परम्परागत रूप से गुरु से दीक्षा अपेक्षित होती है। यहाँ प्रचलित रूप श्रद्धा एवं अध्ययन हेतु दिए गए हैं।",
            "Note: the full sadhana mantras of the Mahavidyas vary by lineage and traditionally require diksha (initiation) from a guru. The commonly transmitted forms are given here for reverence and study."
          )}
        </p>
      </div>
    </div>
  );
}
